import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatRadioModule } from '@angular/material/radio';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { SelectPesquisavelComponent } from '@shared/components/select-pesquisavel/select-pesquisavel.component';
import { PerfilOptionStruct } from '@shared/services/structs/usuario/perfil-option.struct';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { ConfigKanbanService } from '@shared/services/apis/servico/config-kanban.service';
import { MotivoTransicaoService } from '@shared/services/apis/servico/motivo-transicao.service';
import { EtapaKanbanSalvarRequest } from '@shared/services/requests/servico/etapa-kanban-salvar.request';
import { EtapaKanbanStruct } from '@shared/services/structs/servico/etapa-kanban.struct';
import { KanbanCor } from '@shared/services/models/servico/kanban-cor.model';
import { KanbanIcone } from '@shared/services/models/servico/kanban-icone.model';
import { MotivoTransicao } from '@shared/services/models/servico/motivo-transicao.model';
import { CampoAdicionalStruct } from '@shared/services/structs/servico/campo-adicional.struct';
import { TipoEtapaKanbanEnum } from '@shared/enums/servico/tipo-etapa-kanban.enum';
import { KanbanCorEnum } from '@shared/enums/servico/kanban-cor.enum';
import { KanbanIconeEnum } from '@shared/enums/servico/kanban-icone.enum';

const ROTA_MOTIVOS_TRANSICAO = '/configuracoes/servicos/motivos-transicao';

export interface EtapaKanbanOrdemServicoModalData {
  etapa: EtapaKanbanStruct | null;
  etapas: EtapaKanbanStruct[];
  cores: KanbanCor[];
  icones: KanbanIcone[];
  perfis: PerfilOptionStruct[];
  camposAdicionais: CampoAdicionalStruct[];
}

export type EtapaKanbanOrdemServicoModalResult =
  | { action: 'saved' }
  | { action: 'delete'; etapa: EtapaKanbanStruct }
  | undefined;

@Component({
  selector: 'app-etapa-kanban-ordem-servico-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatFormFieldModule,
    MatSlideToggleModule,
    MatRadioModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    AlertCardComponent,
    SelectPesquisavelComponent,
  ],
  templateUrl: './etapa-kanban-ordem-servico-modal.component.html',
  styleUrls: ['./etapa-kanban-ordem-servico-modal.component.css'],
})
export class EtapaKanbanOrdemServicoModalComponent implements OnInit {
  public readonly tamanhoMaximoNome = 60;

  public isEdicao = false;
  public isSaving = false;

  public nome = '';
  public idKanbanCor = 1;
  public idKanbanIcone = 1;
  public isInicial = false;
  public isFinal = false;

  public visibilidadeTodos = true;
  public visibilidadeControl = new FormControl<number[]>([], { nonNullable: true });

  public camposControl = new FormControl<number[]>([], { nonNullable: true });

  public isMotivoObrigatorio = false;
  public motivosControl = new FormControl<number[]>([], { nonNullable: true });
  public motivosAtivos: MotivoTransicao[] = [];
  public isFalhaMotivos = false;

  public movControls = new Map<number, FormControl<number[]>>();

  constructor(
    public dialogRef: MatDialogRef<EtapaKanbanOrdemServicoModalComponent, EtapaKanbanOrdemServicoModalResult>,
    @Inject(MAT_DIALOG_DATA) public data: EtapaKanbanOrdemServicoModalData,
    private configKanbanService: ConfigKanbanService,
    private motivoTransicaoService: MotivoTransicaoService,
    private alertService: AlertService,
    private router: Router
  ) {}

  ngOnInit(): void {
    const s = this.data.etapa;
    this.isEdicao = !!s;
    if (s) {
      this.nome = s.nome;
      this.idKanbanCor = s.idKanbanCor;
      this.idKanbanIcone = s.idKanbanIcone;
      this.isInicial = s.isInicial;
      this.isFinal = s.isFinal;
      this.visibilidadeTodos = s.idsPerfilVisibilidade.length === 0;
      this.visibilidadeControl.setValue([...s.idsPerfilVisibilidade]);
      this.camposControl.setValue([...s.idsCampoAdicional]);
      this.isMotivoObrigatorio = s.isMotivoObrigatorio;
      this.motivosControl.setValue([...s.idsMotivoTransicao]);
      for (const mov of s.movimentacoes) {
        this.movControls.set(
          mov.idEtapaKanbanDestino,
          new FormControl<number[]>([...mov.idsPerfil], { nonNullable: true })
        );
      }
    } else {
      this.idKanbanCor = KanbanCorEnum.Azul;
      this.idKanbanIcone = KanbanIconeEnum.Schedule;
    }

    this.carregarMotivos();
  }

  public get isFixo(): boolean {
    return this.data.etapa?.isFixo ?? false;
  }

  public get isEspecial(): boolean {
    return this.data.etapa?.idTipoEtapaKanban === TipoEtapaKanbanEnum.Especial;
  }

  public get numeroPassoVisibilidade(): number {
    return this.isEspecial ? 2 : 3;
  }

  public get tooltipInicialBloqueado(): string {
    if (this.data.etapa?.isInicial) return 'Marque outra etapa como inicial para desmarcar esta.';
    if (this.data.etapa && !this.data.etapa.isAtivo) return 'Ative a etapa antes de marcá-la como inicial.';
    return '';
  }

  public get inicialAtual(): EtapaKanbanStruct | undefined {
    if (!this.isInicial) return undefined;
    const selfId = this.data.etapa?.idEtapaKanban ?? 0;
    return this.data.etapas.find(e => e.isInicial && e.idEtapaKanban !== selfId);
  }

  public get destinos(): EtapaKanbanStruct[] {
    const selfId = this.data.etapa?.idEtapaKanban ?? 0;
    return this.data.etapas.filter(e => e.idEtapaKanban !== selfId);
  }

  public isDestinoEspecial(etapa: EtapaKanbanStruct): boolean {
    return etapa.idTipoEtapaKanban === TipoEtapaKanbanEnum.Especial;
  }

  public get qtdMovimentacoes(): number {
    return this.movControls.size;
  }

  public isDestino(idEtapaKanban: number): boolean {
    return this.movControls.has(idEtapaKanban);
  }

  public toggleDestino(idEtapaKanban: number, checked: boolean): void {
    if (checked) {
      if (!this.movControls.has(idEtapaKanban)) {
        this.movControls.set(idEtapaKanban, new FormControl<number[]>([], { nonNullable: true }));
      }
    } else {
      this.movControls.delete(idEtapaKanban);
    }
  }

  public perfilControl(idEtapaKanban: number): FormControl<number[]> {
    return this.movControls.get(idEtapaKanban)!;
  }

  public get camposOptions(): { idCampoAdicional: number; nome: string; tipo: string }[] {
    return this.data.camposAdicionais.map(c => ({
      idCampoAdicional: c.idCampoAdicional,
      nome: c.nome,
      tipo: c.descricaoTipoCampoAdicional ?? '',
    }));
  }

  private carregarMotivos(): void {
    this.motivoTransicaoService.getAll('', true, 0, 500).subscribe({
      next: res => {
        if (res.isError) {
          this.isFalhaMotivos = true;
          this.alertService.show('Aviso', res.errorDescription, AlertType.warning);
          return;
        }
        this.motivosAtivos = res.listaMotivos || [];
      },
      error: () => {
        this.isFalhaMotivos = true;
        this.alertService.show(
          'Aviso',
          'Não foi possível carregar os motivos de transição agora. Tente novamente em alguns instantes.',
          AlertType.warning
        );
      },
    });
  }

  public get resumoMotivos(): string {
    const qtd = this.motivosControl.value.length;
    if (qtd === 0) {
      if (this.isFalhaMotivos) return 'Não foi possível carregar os motivos de transição.';
      return this.motivosAtivos.length
        ? 'Todos os motivos ativos ficam disponíveis nesta etapa.'
        : 'Nenhum motivo ativo cadastrado. Cadastre em Gerenciar motivos antes de usar.';
    }
    return `${qtd} motivo(s) selecionado(s).`;
  }

  public gerenciarMotivos(): void {
    this.dialogRef.close(undefined);
    this.router.navigate([ROTA_MOTIVOS_TRANSICAO]);
  }

  public selecionarCor(id: number): void {
    this.idKanbanCor = id;
  }

  public selecionarIcone(id: number): void {
    this.idKanbanIcone = id;
  }

  public iconeNome(idKanbanIcone: number): string {
    return this.data.icones.find(i => i.idKanbanIcone === idKanbanIcone)?.nomeIcone ?? 'label';
  }

  public corPorId(idKanbanCor: number): KanbanCor | undefined {
    return this.data.cores.find(c => c.idKanbanCor === idKanbanCor);
  }

  public corSelecionada(): KanbanCor | undefined {
    return this.corPorId(this.idKanbanCor);
  }

  public salvar(): void {
    const nome = this.nome.trim();
    if (!nome) {
      this.alertService.show('Aviso', 'Informe o nome da etapa.', AlertType.warning);
      return;
    }

    if (!this.isFixo) {
      const selfId = this.data.etapa?.idEtapaKanban ?? 0;
      const nomeDuplicado = this.data.etapas.some(
        e => e.idEtapaKanban !== selfId && e.nome.trim().toLowerCase() === nome.toLowerCase()
      );
      if (nomeDuplicado) {
        this.alertService.show('Aviso', 'Já existe uma etapa com este nome.', AlertType.warning);
        return;
      }
    }

    const request: EtapaKanbanSalvarRequest = {
      idEtapaKanban: this.data.etapa?.idEtapaKanban ?? null,
      nome,
      idKanbanCor: this.idKanbanCor,
      idKanbanIcone: this.idKanbanIcone,
      isInicial: !this.isEspecial && this.isInicial,
      isFinal: !this.isEspecial && this.isFinal,
      idsPerfilVisibilidade: this.visibilidadeTodos ? [] : this.visibilidadeControl.value,
      movimentacoes: this.isEspecial
        ? []
        : Array.from(this.movControls.entries()).map(([idDestino, ctrl]) => ({
            idEtapaKanbanDestino: idDestino,
            idsPerfil: ctrl.value,
          })),
      idsCampoAdicional: this.isEspecial ? [] : this.camposControl.value,
      isMotivoObrigatorio: !this.isEspecial && this.isMotivoObrigatorio,
      idsMotivoTransicao: !this.isEspecial && this.isMotivoObrigatorio ? this.motivosControl.value : [],
    };

    this.isSaving = true;
    this.configKanbanService.salvarEtapa(request).subscribe({
      next: res => {
        this.isSaving = false;
        if (res.isError) {
          this.alertService.show('Aviso', res.errorDescription, AlertType.warning);
          return;
        }
        this.alertService.show(
          'Sucesso',
          this.isEdicao ? `Etapa "${nome}" atualizada.` : `Etapa "${nome}" criada.`,
          AlertType.success
        );
        this.dialogRef.close({ action: 'saved' });
      },
      error: () => {
        this.isSaving = false;
        this.alertService.show(
          'Aviso',
          'Não foi possível salvar a etapa agora. Tente novamente em alguns instantes.',
          AlertType.warning
        );
      },
    });
  }

  public excluir(): void {
    if (!this.data.etapa) return;
    this.dialogRef.close({ action: 'delete', etapa: this.data.etapa });
  }

  public cancelar(): void {
    this.dialogRef.close(undefined);
  }
}
